import { supabase } from '../lib/supabase';
import { activityService } from './activityService';

export const assignmentService = {
  async getTodayAssignment(userId) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_worker_today_assignment', { p_user_id: userId });

      if (error) throw error;
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async ensureTodayAssignment(userId, companyId) {
    try {
      // 1. Check if assignment already exists for today
      const { data: existing, error: fetchError } = await supabase
        ?.rpc('get_worker_today_assignment', { p_user_id: userId });

      if (fetchError) throw fetchError;

      if (existing?.[0]) {
        return { data: existing?.[0], error: null, created: false };
      }

      // 2. No assignment — find the first active site for this company
      const { data: sites, error: siteError } = await supabase
        ?.from('sites')
        ?.select('id, site_name, company_id')
        ?.eq('status', 'active')
        ?.eq('company_id', companyId)
        ?.limit(1);

      if (siteError) throw siteError;

      if (!sites || sites?.length === 0) {
        // No active sites — return null gracefully
        return { data: null, error: null, created: false };
      }

      const site = sites?.[0];
      const today = new Date()?.toISOString()?.split('T')?.[0];

      // 3. Insert assignment record (worker inserts their own)
      const { data: inserted, error: insertError } = await supabase
        ?.from('worker_site_assignments')
        ?.upsert({
          user_id: userId,
          site_id: site?.id,
          company_id: companyId,
          assignment_date: today,
          assigned_by: userId,
        }, {
          onConflict: 'user_id,assignment_date',
        })
        ?.select(`
          id,
          site_id,
          assignment_date,
          sites ( site_name )
        `)
        ?.single();

      if (insertError) throw insertError;

      return {
        data: {
          assignment_id: inserted?.id,
          site_id: inserted?.site_id,
          site_name: inserted?.sites?.site_name,
          assignment_date: inserted?.assignment_date,
        },
        error: null,
        created: true,
      };
    } catch (error) {
      return { data: null, error: { message: error?.message }, created: false };
    }
  },

  async setWorkerAssignment(userId, siteId, companyId, assignmentDate) {
    try {
      const { data, error } = await supabase
        ?.from('worker_site_assignments')
        ?.upsert({
          user_id: userId,
          site_id: siteId,
          company_id: companyId,
          assignment_date: assignmentDate,
          assigned_by: (await supabase?.auth?.getUser())?.data?.user?.id,
          updated_at: new Date()?.toISOString()
        }, {
          onConflict: 'user_id,assignment_date'
        })
        ?.select(`
          *,
          worker:user_profiles!worker_site_assignments_user_id_fkey(id, full_name),
          site:sites!worker_site_assignments_site_id_fkey(id, site_name)
        `)
        ?.single();

      if (error) throw error;

      // Log activity
      if (siteId) {
        const workerName = data?.worker?.full_name || 'Unknown worker';
        const siteName = data?.site?.site_name || 'Unknown site';
        await activityService?.createActivity({
          companyId,
          siteId,
          activityType: 'worker_assigned',
          title: `Worker assigned: ${workerName}`,
          description: `${workerName} assigned to ${siteName} for ${assignmentDate}`,
          relatedEntityId: userId,
          metadata: { workerId: userId, workerName, siteName, assignmentDate }
        });
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async removeWorkerAssignment(userId, assignmentDate) {
    try {
      const { error } = await supabase
        ?.from('worker_site_assignments')
        ?.delete()
        ?.eq('user_id', userId)
        ?.eq('assignment_date', assignmentDate);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: { message: error?.message } };
    }
  },

  async getAllTodayAssignments() {
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { data, error } = await supabase
        ?.from('worker_site_assignments')
        ?.select(`
          id,
          user_id,
          site_id,
          assignment_date,
          sites (
            site_name
          )
        `)
        ?.eq('assignment_date', today);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  /**
   * Other people scheduled on the same site as the caller today.
   * Returns [{ id, full_name, role }] excluding the caller themselves.
   *
   * Backed by the get_others_on_site_today SECURITY DEFINER RPC. The
   * RPC handles the access gate (caller must be assigned to the site
   * that day, or be the site_manager) and unions worker_site_assignments
   * + site_manager_id internally. The previous JS-side embed approach
   * was RLS-denied for workers because user_profiles.company_members_view_profiles
   * blocks workers from reading peers' profiles — the embed silently
   * returned null and the dedup loop dropped every peer.
   *
   * The excludeUserId argument is retained for backwards compatibility
   * with the existing call site but is ignored — the RPC excludes the
   * caller (auth.uid()) server-side.
   */
  // eslint-disable-next-line no-unused-vars
  async getOthersOnSiteToday(siteId, excludeUserId) {
    try {
      if (!siteId) return { data: [], error: null };
      const today = new Date()?.toISOString()?.split('T')?.[0];

      const { data, error } = await supabase
        ?.rpc('get_others_on_site_today', { p_site_id: siteId, p_date: today });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[assignmentService] getOthersOnSiteToday failed:', { code: error?.code, message: error?.message });
      return { data: [], error };
    }
  }
};